"use client";

import { SiteLink } from "@i18n/SiteLink";
import { promptGeneratorSignupUrl } from "@repo/core/client";
import { Button } from "@ui/components/button";
import { CheckIcon } from "lucide-react";

/**
 * "Track these personas": sign up and the claim step imports this generation.
 * The href carries the generation id in the claim PATH
 * (`/auth/signup?redirectTo=/app/prompt-generator-claim/<id>`), built by the
 * same helper the results email uses.
 */
export function TrackBuyersCta({
	generationId,
	promptCount,
	personaCount,
	onClick,
}: {
	generationId: string;
	promptCount: number;
	personaCount: number;
	onClick?: () => void;
}) {
	const withPersonas = personaCount > 0;
	const points = withPersonas
		? [
				"Your prompts and personas come with you. No retyping.",
				"Daily answers from ChatGPT, Gemini, and Google AI Overviews, plus each persona's own answers from ChatGPT and Gemini.",
				"See which personas AI recommends you to, and which it points elsewhere.",
			]
		: [
				"Your prompts come with you. No retyping.",
				"Daily answers from ChatGPT, Gemini, and Google AI Overviews.",
				"Add personas any time to see how answers change with who's asking.",
			];

	return (
		<div className="rounded-xl border-2 border-primary/20 bg-primary/5 p-6">
			<h3 className="text-lg font-semibold">
				{withPersonas
					? "Track how AI answers these personas, every day"
					: "Track AI visibility for these prompts over time"}
			</h3>
			<p className="mt-2 text-sm text-muted-foreground">
				{withPersonas
					? `Sign up free and we'll import these ${promptCount} prompts and ${personaCount} personas into your account. Then we track how AI answers each persona daily.`
					: `Sign up free and we'll import these ${promptCount} prompts into your account, then run them against AI every day.`}
			</p>
			<ul className="mt-4 space-y-1.5 text-sm">
				{points.map((point) => (
					<li key={point} className="flex gap-2">
						<CheckIcon
							className="mt-0.5 size-4 shrink-0 text-primary"
							aria-hidden
						/>
						<span>{point}</span>
					</li>
				))}
			</ul>
			<div className="mt-5 flex flex-col gap-3 sm:flex-row">
				<Button asChild size="lg" variant="primary">
					<SiteLink
						href={promptGeneratorSignupUrl(generationId)}
						onClick={onClick}
						data-testid="track-buyers-cta"
					>
						{withPersonas
							? "Track These Personas"
							: "Track These Prompts"}
					</SiteLink>
				</Button>
				<Button asChild size="lg" variant="outline">
					<SiteLink href="/pricing">See Pricing</SiteLink>
				</Button>
			</div>
			<p className="mt-3 text-xs text-muted-foreground">
				Free to sign up. Your results stay on this page either way.
			</p>
		</div>
	);
}
